import { useState } from 'react';
import { AlertTriangle, XCircle, CreditCard, Loader2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useLanguage } from '../../../contexts/LanguageContext';
import { billingService } from '../../../services/billingService';
import { toastService } from '../../../services/toastService';
import { Logger } from '../../../utils/logger';

const PAYMENT_GRACE_DAYS = 7;

/**
 * Bandeau d'alerte affiché en haut du dashboard quand le renouvellement Stripe a échoué.
 *
 * [BETA] Ce composant est invisible pour les beta users car leur stripe_payment_status
 * est null (upgrade admin manuel, aucune subscription Stripe active).
 * Il ne s'affiche que pour les utilisateurs payants dont le paiement est en échec.
 *
 * Logique :
 * - stripePaymentStatus === 'failed' → afficher bandeau avec deadline (paymentFailedAt + 7j)
 * - Si deadline non dépassée → "Mettez à jour avant le [date]" + bouton Stripe Portal
 * - Si deadline dépassée → "Accès Pro suspendu" (le backend a déjà downgradé au /me suivant)
 */
export function PaymentAlertBanner() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // [BETA] Invisible si pas d'échec de paiement Stripe
  if (user?.stripePaymentStatus !== 'failed') {
    return null;
  }

  const paymentFailedAt = user.paymentFailedAt ? new Date(user.paymentFailedAt) : null;
  const graceDeadline = paymentFailedAt
    ? new Date(paymentFailedAt.getTime() + PAYMENT_GRACE_DAYS * 24 * 60 * 60 * 1000)
    : null;
  const isExpired = graceDeadline ? graceDeadline < new Date() : false;

  const formattedDeadline = graceDeadline
    ? graceDeadline.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const handleUpdatePayment = async () => {
    setIsRedirecting(true);
    try {
      const { url } = await billingService.createPortalSession();
      window.location.href = url;
    } catch (error) {
      Logger.error('[PaymentAlertBanner] Failed to create portal session', error);
      toastService.error(t.billing.paymentFailed);
      setIsRedirecting(false);
    }
  };

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 text-sm font-medium"
      style={{
        backgroundColor: isExpired
          ? 'var(--color-error-bg, rgba(239,68,68,0.1))'
          : 'var(--color-warning-bg, rgba(245,158,11,0.1))',
        borderBottom: `1px solid ${isExpired ? 'var(--color-error)' : 'var(--color-warning)'}`,
        color: isExpired ? 'var(--color-error)' : 'var(--color-warning)',
      }}
    >
      {isExpired ? (
        <XCircle size={16} className="shrink-0" />
      ) : (
        <AlertTriangle size={16} className="shrink-0" />
      )}

      <span className="flex-1">
        {isExpired
          ? t.billing.paymentFailedExpired
          : t.billing.paymentFailedDescription.replace('{date}', formattedDeadline)}
      </span>

      {!isExpired && (
        <button
          onClick={handleUpdatePayment}
          disabled={isRedirecting}
          className="flex items-center gap-1.5 shrink-0 px-3 py-1 rounded text-xs font-semibold transition-opacity hover:opacity-80 disabled:opacity-50"
          style={{
            backgroundColor: 'var(--color-warning)',
            color: '#fff',
          }}
        >
          {isRedirecting ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <CreditCard size={12} />
          )}
          {t.billing.updatePaymentMethod}
        </button>
      )}
    </div>
  );
}
